import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { passportMeta, type PassportStage } from '@/mocks/trace-passport';

interface PassportStageListProps {
  stages: PassportStage[];
}

function StageCard({ stage, index }: { stage: PassportStage; index: number }) {
  const [open, setOpen] = useState(index === 0);
  const hasSample = Boolean(stage.sample);
  const iconBox = index % 2 === 0 ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700';

  return (
    <div className="relative overflow-hidden rounded-2xl border border-accent-200 bg-accent-50/60 p-4 md:p-5">
      <span className="pointer-events-none absolute -top-1 right-3 font-heading font-bold text-5xl text-accent-100 select-none">
        {stage.no}
      </span>

      <button
        type="button"
        onClick={() => hasSample && setOpen((v) => !v)}
        aria-expanded={open}
        className={`relative w-full text-left ${hasSample ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-xl ${iconBox}`}>
            <i className={`${stage.icon} text-xl`}></i>
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold">
            {stage.no}
          </span>
          {hasSample && (
            <i
              className={`ml-auto ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 ${
                open ? 'rotate-180' : ''
              }`}
            ></i>
          )}
        </div>

        <h3 className="mt-3 font-heading font-bold text-lg text-foreground-950">{stage.title}</h3>
        <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{stage.desc}</p>
      </button>

      <div className="mt-3.5 pt-3.5 border-t border-accent-200/70">
        <ul className="space-y-2.5">
          {stage.points.map((point) => (
            <li key={point} className="flex items-start gap-2 text-sm text-foreground-700">
              <i className="ri-leaf-line mt-0.5 text-secondary-500"></i>
              <span className="leading-relaxed">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {hasSample && open && stage.sample && (
        <div className="mt-4 rounded-xl bg-background-50 border border-accent-200/70 p-3.5">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-500">
            <i className="ri-image-2-line"></i>
            {passportMeta.sampleLabel}
          </div>

          <div className="mt-3 w-full h-[168px] rounded-lg overflow-hidden bg-background-100">
            <img
              src={stage.sample.photo}
              alt={stage.sample.photoAlt}
              title={stage.sample.photoAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{stage.sample.note}</p>

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-background-100 text-xs text-foreground-600 whitespace-nowrap">
              {stage.sample.date}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium whitespace-nowrap">
              {stage.sample.tag}
            </span>
          </div>

          <div className="mt-2.5 flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-map-pin-2-line"></i>
            {stage.sample.place}
          </div>
        </div>
      )}

      <div
        className={`mt-4 rounded-lg border py-2.5 flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap ${
          stage.recordCount > 0
            ? 'border-accent-200 bg-background-50 text-secondary-700'
            : 'border-background-200 bg-background-50 text-foreground-400'
        }`}
      >
        <i className={stage.recordCount > 0 ? 'ri-checkbox-circle-line' : 'ri-time-line'}></i>
        {stage.recordCount > 0 ? `已留痕 ${stage.recordCount} 条` : '暂无留痕'}
      </div>
    </div>
  );
}

export default function PassportStageList({ stages }: PassportStageListProps) {
  return (
    <section className="px-4 py-6">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
          <i className="ri-bookmark-line text-lg"></i>
        </span>
        <div className="min-w-0">
          <h2 className="font-heading font-bold text-xl text-foreground-950">{passportMeta.sectionTitle}</h2>
          <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{passportMeta.sectionSubtitle}</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {stages.map((stage, index) => (
          <Reveal key={stage.no} delay={Math.min(index * 50, 200)}>
            <StageCard stage={stage} index={index} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}